import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getCareers, createCareer, updateCareer, deleteCareer } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Eye, EyeOff, MapPin, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const jobTypes = ['full-time', 'part-time', 'contract', 'remote'];
const departments = ['Engineering', 'Marketing', 'Design', 'Sales', 'Operations', 'Content', 'Product'];

export default function AdminCareers() {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'full-time',
    description: '',
    requirements: '',
    active: true,
  });

  useEffect(() => {
    loadCareers();
  }, []);

  const loadCareers = async () => {
    try {
      const res = await getCareers(false);
      setCareers(res.data);
    } catch (err) {
      toast.error('Failed to load careers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      requirements: formData.requirements.split('\n').map(r => r.trim()).filter(Boolean),
    };

    try {
      if (editingCareer) {
        await updateCareer(editingCareer.id, data);
        toast.success('Job posting updated');
      } else {
        await createCareer(data);
        toast.success('Job posting created');
      }
      loadCareers();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save job posting');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (career) => {
    setEditingCareer(career);
    setFormData({
      title: career.title,
      department: career.department,
      location: career.location,
      type: career.type,
      description: career.description,
      requirements: career.requirements?.join('\n') || '',
      active: career.active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      await deleteCareer(id);
      toast.success('Job posting deleted');
      loadCareers();
    } catch (err) {
      toast.error('Failed to delete job posting');
    }
  };

  const toggleActive = async (career) => {
    try {
      await updateCareer(career.id, { 
        ...career, 
        active: !career.active,
        requirements: career.requirements || []
      });
      toast.success(career.active ? 'Job posting hidden' : 'Job posting visible');
      loadCareers();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const resetForm = () => {
    setEditingCareer(null);
    setFormData({
      title: '',
      department: '',
      location: '',
      type: 'full-time',
      description: '',
      requirements: '',
      active: true,
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'full-time': return 'bg-green-500/10 text-green-500';
      case 'part-time': return 'bg-blue-500/10 text-blue-500';
      case 'contract': return 'bg-yellow-500/10 text-yellow-500';
      case 'remote': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-[#ff5a1f]/10 text-[#ff5a1f]';
    }
  };

  return (
    <AdminLayout title="Careers Management">
      <div className="space-y-6" data-testid="admin-careers">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Job Openings</h1>
            <p className="text-[#888] text-sm">Manage career opportunities</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-career-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Job
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingCareer ? 'Edit Job Posting' : 'New Job Posting'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="career-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Job Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Department *</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      required
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    >
                      <option value="">Select department</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Job Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      required
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    >
                      {jobTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="e.g., Remote, Mumbai, Bangalore"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={5}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                    placeholder="Describe the role, responsibilities, and what makes it exciting..."
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Requirements (one per line)</label>
                  <textarea
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    rows={5}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                    placeholder="3+ years of experience&#10;Strong problem-solving skills&#10;Excellent communication"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 accent-[#ff5a1f]"
                  />
                  <label htmlFor="active" className="text-white text-sm">Active (show on careers page)</label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingCareer ? 'Update' : 'Publish'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : careers.length > 0 ? (
          <div className="space-y-4">
            {careers.map((career) => (
              <div
                key={career.id}
                className={`bg-[#0A0A0A] border p-6 ${
                  career.active ? 'border-[#1F1F1F]' : 'border-[#1F1F1F] opacity-60'
                }`}
                data-testid={`career-item-${career.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-syne font-bold text-white text-lg">{career.title}</h3>
                      {career.active ? (
                        <span className="text-green-500 text-xs flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="text-[#555] text-xs flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#888]">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {career.department}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {career.location}
                      </span>
                      <span className={`px-3 py-1 text-xs font-medium uppercase ${getTypeColor(career.type)}`}>
                        {career.type}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleActive(career)}
                      className="text-[#888] hover:text-white p-2 border border-[#333] hover:border-[#555]"
                      title={career.active ? 'Hide' : 'Show'}
                    >
                      {career.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(career)}
                      className="text-[#888] hover:text-[#ff5a1f] p-2 border border-[#333] hover:border-[#ff5a1f]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(career.id)}
                      className="text-[#888] hover:text-red-500 p-2 border border-[#333] hover:border-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No job postings yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Create your first job posting
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
